import { formatDateForEditDisplay } from "../../util/dateUtil";

export const QUALITY_FRAMEWORK_TABS = [
  { key: "all", label: "All" },
  { key: "assigned", label: "Assigned" },
  { key: "completed", label: "Completed" },
];

export const CELL_TRUNCATE_LENGTH = 15;

export const QUALITY_FRAMEWORK_EXPORT_COLUMNS = [
  "Order ID",
  "Client Name",
  "Engagement Name",
  "Super Region",
  "Region",
  "Analyzer type",
  "Preparer",
  "Reviewer",
  "Assigned to",
  "Analyzer Delivery Date",
  "Checks Status",
];

export const mapQualityRequestForExport = (row = {}) => ({
  "Order ID": row.orderRequestId != null ? `AX-${row.orderRequestId}` : "",
  "Client Name": row.clientName || "",
  "Engagement Name": row.engagementName || "",
  "Super Region": row.region || "",
  "Region": row.countryName || "",
  "Analyzer type": row.analyzerType || "",
  Preparer: row.preparer || "",
  Reviewer: row.reviewer || "",
  "Assigned to": row.assignedTo || "",
  "Analyzer Delivery Date": formatDateForEditDisplay(row.deliveryDate) || "-",
  "Checks Status": row.status || "",
});

export const truncateText = (value, maxLength = CELL_TRUNCATE_LENGTH) => {
  const text = String(value ?? "");
  return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
};

// Maps a quality framework order from the API to the shape used by the table
export const mapQualityRequest = (order = {}, index = 0) => ({
  id: order.orderGuId || order.orderRequestId || index + 1,
  serialNumber: index + 1,
  orderGuId: order.orderGuId || "",
  orderRequestId: order.orderRequestId ?? null,
  axonId: order.axonId ?? order.axonID ?? order.axon_id ?? order.orderRequestId ?? null,
  orderId: order.orderId || "-",
  clientName: order.clientName || "-",
  engagementName: order.engagementName || "-",
  area: order.regionName || "-",
  superRegion: order.superRegionName || "-",
  region: order.superRegionName || "-",
  countryName: order.countryName || "",
  analyzerType: order.analyzerType || "-",
  preparer: order.preparer || "-",
  reviewer: order.reviewer || "-",
  assignedTo: order.assignedTo || "-",
  deliveryDate: order.analyzerDeliveryDate || "",
  status: order.checkStatus || "",
  canSendChecks: order.canSendChecks === true,
  canViewResponse: order.canViewResponse === true,
});

const normalizeRoleName = (role) => {
  if (typeof role === "string") {
    return role.trim().toLowerCase();
  }

  if (role && typeof role === "object") {
    return String(role.roleName || role.name || "")
      .trim()
      .toLowerCase();
  }

  return "";
};

const isRoleActive = (role) => {
  if (!role || typeof role !== "object") {
    return true;
  }

  if (typeof role.isActive === "boolean") {
    return role.isActive;
  }

  if (typeof role.status === "boolean") {
    return role.status;
  }

  if (typeof role.status === "string") {
    return role.status.trim().toLowerCase() !== "inactive";
  }

  return true;
};

export const canCreateQualityRequest = (selectedRole, userRoles) => {
  // Always check all userRoles — quality roles are hidden from the role selector
  // so selectedRole will never be a quality role
  const roles = Array.isArray(userRoles) ? userRoles : [];

  return roles.some(
    (role) => isRoleActive(role) && normalizeRoleName(role) === "quality spoc",
  );
};

// Gates direct URL access to Quality Framework pages to Quality SPOC/Quality Reviewer roles
export const hasQualityFrameworkAccess = (userRoles) => {
  const roles = Array.isArray(userRoles) ? userRoles : [];

  return roles.some((role) => {
    if (!isRoleActive(role)) {
      return false;
    }

    const normalizedRoleName = normalizeRoleName(role);
    return (
      normalizedRoleName === "quality spoc" ||
      normalizedRoleName === "quality reviewer"
    );
  });
};

export const hasQualitySpocRole = (userRoles) => {
  const roles = Array.isArray(userRoles) ? userRoles : [];

  return roles.some(
    (role) =>
      isRoleActive(role) && normalizeRoleName(role) === "quality spoc",
  );
};

export const buildTabCounts = (requests = []) => {
  const total = requests.length;
  const assigned = requests.filter(
    (request) => String(request?.status || "").toLowerCase() === "assigned",
  ).length;
  const completed = requests.filter(
    (request) => String(request?.status || "").toLowerCase() === "completed",
  ).length;

  return {
    all: total,
    assigned,
    completed,
  };
};

export const filterRequests = (requests = [], activeTab = "all", searchText = "") => {
  const normalizedSearch = searchText.trim().toLowerCase();

  return requests
    .filter((request) => {
      if (activeTab === "all") {
        return true;
      }

      return String(request?.status || "").toLowerCase() === activeTab;
    })
    .filter((request) => {
      if (!normalizedSearch) {
        return true;
      }

      const textToSearch = [
        request?.orderId,
        request?.orderRequestId != null ? `AX-${request.orderRequestId}` : null,
        request?.clientName,
        request?.engagementName,
        request?.region,
        request?.analyzerType,
        request?.preparer,
        request?.reviewer,
        request?.assignedTo,
        request?.status,
        formatDateForEditDisplay(request?.deliveryDate),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return textToSearch.includes(normalizedSearch);
    });
};
